import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storeExpensesAPI, storeTerminalsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Receipt, TrendingUp, TrendingDown, Calendar,
  Edit, Trash2, Search, Filter, DollarSign, CreditCard, Monitor
} from 'lucide-react';

const StoreExpenses = () => {
  const { user } = useAuth();
  const storeId = user?.entity_id;

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    terminal_id: '',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    if (storeId) {
      loadData();
      loadTerminals();
    }
  }, [storeId, filterCategory, filterFromDate, filterToDate]);

  const loadTerminals = async () => {
    try {
      const res = await storeTerminalsAPI.list(storeId);
      // API returns {terminals: [...], count: n}
      const terminalsList = res.data?.terminals || res.data || [];
      setTerminals(Array.isArray(terminalsList) ? terminalsList : []);
    } catch (error) {
      console.error('Error loading terminals:', error);
      setTerminals([]);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, expensesRes, summaryRes] = await Promise.all([
        storeExpensesAPI.getCategories(),
        storeExpensesAPI.list({
          store_id: storeId,
          category: filterCategory !== 'all' ? filterCategory : undefined,
          from_date: filterFromDate || undefined,
          to_date: filterToDate || undefined
        }),
        storeExpensesAPI.getSummary(storeId)
      ]);
      
      setCategories(categoriesRes.data);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        category: expense.category,
        description: expense.description,
        amount: expense.amount.toString(),
        expense_date: expense.expense_date,
        payment_mode: expense.payment_mode || 'cash',
        terminal_id: expense.terminal_id || '',
        reference_number: expense.reference_number || '',
        notes: expense.notes || ''
      });
    } else {
      setEditingExpense(null);
      setFormData({
        category: '',
        description: '',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        payment_mode: 'cash',
        terminal_id: '',
        reference_number: '',
        notes: ''
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.description || !formData.amount) {
      toast.error('Please fill all required fields');
      return;
    }

    // Require terminal selection for cash expenses
    if (formData.payment_mode === 'cash' && !formData.terminal_id) {
      toast.error('Please select a terminal/source for cash expenses');
      return;
    }

    try {
      const terminal = terminals.find(t => t.terminal_id === formData.terminal_id);
      const payload = {
        ...formData,
        store_id: storeId,
        amount: parseFloat(formData.amount),
        terminal_name: terminal?.terminal_name || null
      };

      if (editingExpense) {
        await storeExpensesAPI.update(editingExpense.id, payload);
        toast.success('Expense updated successfully');
      } else {
        await storeExpensesAPI.create(payload);
        toast.success('Expense added successfully');
      }

      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async () => {
    if (!expenseToDelete) return;
    
    try {
      await storeExpensesAPI.delete(expenseToDelete.id);
      toast.success('Expense deleted successfully');
      setDeleteConfirmOpen(false);
      setExpenseToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  const getPaymentModeLabel = (mode) => {
    const modes = {
      cash: 'Cash',
      upi: 'UPI',
      card: 'Card',
      bank_transfer: 'Bank Transfer'
    };
    return modes[mode] || mode;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Expenses</h1>
          <p className="text-gray-500">Track and manage store operational expenses</p>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary.total)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Previous Month</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary.previous_month_total)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Change</p>
                  <p className={`text-2xl font-bold ${summary.change_percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {summary.change_percentage >= 0 ? '+' : ''}{summary.change_percentage}%
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-full ${summary.change_percentage >= 0 ? 'bg-red-100' : 'bg-green-100'} flex items-center justify-center`}>
                  {summary.change_percentage >= 0 
                    ? <TrendingUp className="w-6 h-6 text-red-600" />
                    : <TrendingDown className="w-6 h-6 text-green-600" />
                  }
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Categories</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.keys(summary.by_category || {}).length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Filter className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[200px]">
              <Label>Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From Date</Label>
              <Input
                type="date"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
                data-testid="filter-from-date"
              />
            </div>
            <div>
              <Label>To Date</Label>
              <Input
                type="date"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
                data-testid="filter-to-date"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                setFilterCategory('all');
                setFilterFromDate('');
                setFilterToDate('');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Expense Records</CardTitle>
          <CardDescription>
            {expenses.length} expense{expenses.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading expenses...</div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No expenses found. Add your first expense!
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Payment Mode</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <TableCell className="font-medium">
                      <div>
                        <div>{new Date(expense.expense_date).toLocaleDateString('en-IN')}</div>
                        <div className="text-xs text-gray-500">
                          {expense.created_at ? new Date(expense.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getCategoryName(expense.category)}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {expense.description}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {expense.payment_mode === 'cash' ? (
                          <DollarSign className="w-4 h-4 text-green-600" />
                        ) : (
                          <CreditCard className="w-4 h-4 text-blue-600" />
                        )}
                        {getPaymentModeLabel(expense.payment_mode)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {expense.terminal_name ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Monitor className="w-3 h-3 text-gray-500" />
                          {expense.terminal_name}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(expense)}
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setExpenseToDelete(expense);
                            setDeleteConfirmOpen(true);
                          }}
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Expense Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? 'Edit Expense' : 'Add New Expense'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Electricity bill for December"
                data-testid="expense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  data-testid="expense-amount-input"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
            </div>

            <div>
              <Label>Payment Mode</Label>
              <Select
                value={formData.payment_mode}
                onValueChange={(value) => setFormData({ ...formData, payment_mode: value, terminal_id: value !== 'cash' ? '' : formData.terminal_id })}
              >
                <SelectTrigger data-testid="expense-payment-mode-select">
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Terminal/Source selection - Required for cash expenses */}
            {formData.payment_mode === 'cash' && (
              <div>
                <Label>Source Terminal *</Label>
                <Select
                  value={formData.terminal_id}
                  onValueChange={(value) => setFormData({ ...formData, terminal_id: value })}
                >
                  <SelectTrigger data-testid="expense-terminal-select">
                    <Monitor className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Select terminal" />
                  </SelectTrigger>
                  <SelectContent>
                    {terminals.map(terminal => (
                      <SelectItem key={terminal.id} value={terminal.terminal_id}>
                        {terminal.terminal_name} ({terminal.terminal_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-amber-600 mt-1">
                  Cash expenses will be deducted from this terminal's register
                </p>
              </div>
            )}

            <div>
              <Label>Reference Number</Label>
              <Input
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="Bill/Receipt number"
                data-testid="expense-reference-input"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                data-testid="expense-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-expense-btn">
                {editingExpense ? 'Update' : 'Save'} Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete this expense record? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreExpenses;
